import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';

const reviews = [
  {
    text: "We fell in love with Padua because of the variety throughout the property — from the olive grove and Spanish-inspired architecture to the mountain views and romantic ceremony spaces. The venue already has so much character that it hardly needs additional decorating. Our wedding was beautiful, and our guests are still talking about it.",
    themes: ["Historic charm", "Olive grove", "Scenic views", "Romantic atmosphere"]
  },
  {
    text: "From our first tour to the wedding day itself, the Padua team made everything feel organized, thoughtful, and stress-free. Every question was answered quickly, the planning process felt seamless, and we always felt supported. The staff truly helped make our day unforgettable.",
    themes: ["Exceptional service", "Planning support", "Stress-free experience"]
  },
  {
    ...{
      text: "Padua feels like a hidden estate tucked away in the hills. The sunset views, beautiful grounds, indoor reception spaces, and incredible food exceeded our expectations. Our guests loved the venue, the atmosphere, and the overall experience.",
      themes: ["Hidden gem", "Food quality", "Guest experience", "Sunset views"]
    }
  }
];

const ReviewsSection = () => {
  return (
    <section className="py-24 bg-padua-ivory relative overflow-hidden">
      {/* Decorative Background Pattern */}
      <div className="absolute inset-0 bg-tile-pattern opacity-[0.03] pointer-events-none" />
      
      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-serif text-padua-charcoal mb-6">
              Loved by Couples and Their Guests
            </h2>
            <p className="text-lg text-padua-charcoal/70 max-w-2xl mx-auto leading-relaxed mb-4">
              Couples consistently mention the venue's historic beauty, attentive team, scenic photo locations, and seamless wedding-day experience.
            </p>
            <div className="flex items-center justify-center gap-2 text-padua-gold/60 text-[10px] font-bold uppercase tracking-[0.2em]">
              <div className="w-8 h-px bg-padua-gold/30" />
              <span>Based on 174 Yelp Reviews</span>
              <div className="w-8 h-px bg-padua-gold/30" />
            </div>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {reviews.map((review, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 }}
              className="group bg-padua-cream flex flex-col h-full rounded-2xl shadow-sm hover:shadow-xl transition-all duration-500 border border-padua-gold/10 overflow-hidden"
            >
              {/* Card Content */}
              <div className="p-8 md:p-10 flex-grow">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex text-padua-gold">
                    {[...Array(5)].map((_, i) => (
                      <SafeIcon key={i} name="Star" className="w-4 h-4 fill-current" />
                    ))}
                  </div>
                </div>

                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-padua-gold mb-4">
                  Verified Yelp Review
                </p>

                <blockquote className="text-lg md:text-xl text-padua-charcoal font-serif italic leading-relaxed mb-8">
                  "{review.text}"
                </blockquote>
              </div>

              {/* Card Footer with Themes */}
              <div className="px-8 pb-10 mt-auto">
                <div className="flex flex-wrap gap-2 pt-6 border-t border-padua-gold/10">
                  {review.themes.map((theme, tIdx) => (
                    <span 
                      key={tIdx} 
                      className="text-[10px] uppercase font-bold tracking-wider text-padua-olive/60 bg-padua-olive/5 px-2 py-1 rounded"
                    >
                      {theme}
                    </span>
                  ))}
                </div>
              </div>

              {/* Spanish Tile Decorative Edge */}
              <div className="h-1.5 w-full bg-padua-gold/20 flex">
                {[...Array(12)].map((_, i) => (
                  <div 
                    key={i} 
                    className={`h-full flex-1 ${i % 2 === 0 ? 'bg-padua-gold/40' : 'bg-transparent'}`} 
                  />
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;